import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const INTEGRATION = '#57BDA2';
const TEXT_MUTED = '#5a5f7d';

interface TreeNode {
  id: number;
  x: number;
  y: number;
  label: string;
  parent: number | null;
  finalX: number;
  finalY: number;
}

export function ProcedureCollapse() {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 320;
    const r = 8;

    // Decision tree nodes (phase 1 positions)
    const nodes: TreeNode[] = [
      { id: 0, x: 160, y: 25, label: 'assess', parent: null, finalX: 48, finalY: 30 },
      { id: 1, x: 90, y: 65, label: 'context?', parent: 0, finalX: 48, finalY: 62 },
      { id: 2, x: 230, y: 65, label: 'risk?', parent: 0, finalX: 48, finalY: 94 },
      { id: 3, x: 50, y: 110, label: 'adapt', parent: 1, finalX: 48, finalY: 126 },
      { id: 4, x: 130, y: 110, label: 'consult', parent: 1, finalX: 48, finalY: 158 },
      { id: 5, x: 200, y: 110, label: 'mitigate', parent: 2, finalX: 48, finalY: 158 },
      { id: 6, x: 270, y: 110, label: 'escalate', parent: 2, finalX: 48, finalY: 158 },
      { id: 7, x: 90, y: 155, label: 'decide', parent: 3, finalX: 48, finalY: 158 },
    ];

    // Checklist items (phase 3)
    const checklist = ['assess', 'context?', 'risk?', 'adapt', 'decide'];

    // Draw tree edges
    const edgeGroup = svg.append('g');
    nodes.forEach((node) => {
      if (node.parent !== null) {
        const parent = nodes[node.parent];
        edgeGroup
          .append('line')
          .attr('class', `edge-${node.id}`)
          .attr('x1', parent.x)
          .attr('y1', parent.y)
          .attr('x2', parent.x)
          .attr('y2', parent.y)
          .attr('stroke', INTEGRATION)
          .attr('stroke-width', 1.5)
          .attr('opacity', 0.5)
          .transition()
          .duration(400)
          .delay(node.id * 100)
          .attr('x2', node.x)
          .attr('y2', node.y);
      }
    });

    // Draw tree nodes
    const nodeGroup = svg.append('g');
    nodes.forEach((node) => {
      const g = nodeGroup.append('g').attr('class', `node-${node.id}`);

      g.append('circle')
        .attr('cx', node.x)
        .attr('cy', node.y)
        .attr('r', 0)
        .attr('fill', INTEGRATION)
        .attr('opacity', 0.8)
        .transition()
        .duration(300)
        .delay(node.id * 100)
        .attr('r', r);

      g.append('text')
        .attr('x', node.x)
        .attr('y', node.y + r + 12)
        .attr('text-anchor', 'middle')
        .attr('font-size', 7.5)
        .attr('font-weight', 500)
        .attr('class', 'viz-text-muted')
        .attr('opacity', 0)
        .text(node.label)
        .transition()
        .duration(200)
        .delay(150 + node.id * 100)
        .attr('opacity', 0.9);
    });

    // Phase 2: Collapse to linear (after 2s)
    const collapseTimer = d3.timeout(() => {
      // Fade non-essential nodes
      [5, 6, 4].forEach((id, i) => {
        nodeGroup.select(`.node-${id}`)
          .transition()
          .duration(400)
          .delay(i * 100)
          .attr('opacity', 0);

        edgeGroup.select(`.edge-${id}`)
          .transition()
          .duration(400)
          .delay(i * 100)
          .attr('opacity', 0);
      });

      // Fade all remaining edges
      edgeGroup
        .transition()
        .duration(500)
        .delay(400)
        .attr('opacity', 0);

      // Move remaining nodes into a vertical line
      const keepIds = [0, 1, 2, 3, 7];
      keepIds.forEach((id, i) => {
        const targetX = 48;
        const targetY = 30 + i * 32;

        nodeGroup.select(`.node-${id}`).select('circle')
          .transition()
          .duration(600)
          .delay(500)
          .ease(d3.easeCubicInOut)
          .attr('cx', targetX)
          .attr('cy', targetY);

        nodeGroup.select(`.node-${id}`).select('text')
          .transition()
          .duration(600)
          .delay(500)
          .ease(d3.easeCubicInOut)
          .attr('x', targetX + 16)
          .attr('y', targetY + 4)
          .attr('text-anchor', 'start');
      });
    }, 2000);

    // Phase 3: Checklist overlay (after 4s)
    const checklistTimer = d3.timeout(() => {
      // Fade old nodes to make room for clean checklist
      nodeGroup
        .transition()
        .duration(400)
        .attr('opacity', 0.15);

      const clGroup = svg.append('g').attr('class', 'checklist');
      const clX = 140;
      const clStartY = 30;
      const clGap = 32;

      checklist.forEach((item, i) => {
        const y = clStartY + i * clGap;

        // Checkbox square
        clGroup
          .append('rect')
          .attr('x', clX)
          .attr('y', y - 7)
          .attr('width', 14)
          .attr('height', 14)
          .attr('rx', 2)
          .attr('fill', 'none')
          .attr('stroke', TEXT_MUTED)
          .attr('stroke-width', 1.5)
          .attr('opacity', 0)
          .transition()
          .duration(300)
          .delay(i * 120)
          .attr('opacity', 0.7);

        // Checkmark
        clGroup
          .append('text')
          .attr('x', clX + 7)
          .attr('y', y + 4)
          .attr('text-anchor', 'middle')
          .attr('font-size', 11)
          .attr('fill', INTEGRATION)
          .attr('opacity', 0)
          .text('\u2713')
          .transition()
          .duration(200)
          .delay(300 + i * 120)
          .attr('opacity', 0.8);

        // Label
        clGroup
          .append('text')
          .attr('x', clX + 22)
          .attr('y', y + 4)
          .attr('text-anchor', 'start')
          .attr('font-size', 10)
          .attr('class', 'viz-text')
          .attr('opacity', 0)
          .text(item)
          .transition()
          .duration(300)
          .delay(i * 120)
          .attr('opacity', 1);
      });

      // "judgment removed" annotation
      clGroup
        .append('text')
        .attr('x', width - 20)
        .attr('y', clStartY + checklist.length * clGap + 10)
        .attr('text-anchor', 'end')
        .attr('font-size', 8)
        .attr('font-style', 'italic')
        .attr('fill', TEXT_MUTED)
        .attr('opacity', 0)
        .text('context removed')
        .transition()
        .duration(400)
        .delay(checklist.length * 120 + 200)
        .attr('opacity', 0.7);
    }, 3800);

    return () => {
      collapseTimer.stop();
      checklistTimer.stop();
      svg.selectAll('*').remove();
    };
  }, []);

  return (
    <div className="viz-container">
      <h4 className="viz-container__title">
        <i className="fa-solid fa-list-check" /> Checklist &mdash; Procedure Dominance
      </h4>
      <svg ref={svgRef} viewBox="0 0 320 200" />
      <p className="viz-container__caption">
        Reliability gained, judgment removed.
      </p>
    </div>
  );
}
